import { ReactNode, useMemo, useState } from 'react';

import { GetScheduleFormsResponseDTO, ScheduleFormDTO } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import formVector from '@/assets/vectors/form.svg';
import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import UndoIcon from '@/components/icons/undo';
import ViewScheduleRecordDrawer from '@/components/schedule-forms/view-schedule-record-drawer';
import { TablePageTable } from '@/components/table-page/table-page-table';

import { columns } from './table-meta';

export const AssetScheduleRecordsTable = ({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
}: {
  notFoundDisplay: ReactNode;
  data: ScheduleFormDTO[];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
}) => {
  const [submission, setSubmission] = useState<GetScheduleFormsResponseDTO['data'][number] | undefined>(undefined);

  const tableColumns = useMemo(() => columns(), []);

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  return (
    <>
      <div className="flex-1 bg-white">
        <TablePageTable<GetScheduleFormsResponseDTO['data'][number]>
          data={data}
          columns={tableColumns}
          notFoundDisplay={notFoundDisplay}
          loading={loading}
          onSortingChange={onSortingChange}
          initialSorting={initialSorting}
          grouping={grouping}
          columnOrder={(columnOrder || []).map((column) => column.id)}
          onRowClick={(row) => setSubmission(row)}
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
          columnVisibility={columnVisibility}
          columnFilters={columnFilters}
          onColumnFiltersChange={onColumnFiltersChange}
        />
      </div>
      <ViewScheduleRecordDrawer open={!!submission} onOpenChange={() => setSubmission(undefined)} scheduleFormId={submission?.id} noTrigger />
    </>
  );
};

export const NoScheduleFormSubmissionAvailableDisplay = () => {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={formVector} alt="Clock" />
      <span className="text-xl leading-[30px] font-medium">No records have been submitted yet</span>
      <div className="flex flex-col gap-0 text-center">
        <span className="text-secondary-text text-sm">
          When your workers submit records they will appear here. To get started, send an access link to users.
        </span>
      </div>
    </div>
  );
};

export const NoScheduleFormSubmissionFoundFromSearchDisplay = () => {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-sm">No schedule form activity match your search</span>
    </div>
  );
};

export const NoScheduleFormSubmissionFoundFromFiltersDisplay = ({ removeColumnFilters }: { removeColumnFilters: () => void }) => {
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl font-medium">No schedule form activity match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
      </div>
    </div>
  );
};
